import React, { useContext, useEffect, useState, useRef, useMemo } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import ListItemButton from "@mui/material/ListItemButton";
import Typography from "@mui/material/Typography";
import Collapse from "@mui/material/Collapse";
import Tooltip from "@mui/material/Tooltip";
import InputBase from "@mui/material/InputBase";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";

import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import BarChartIcon from "@mui/icons-material/BarChart";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";

import isotipo from "../assets/isotipo-shessai.png";

import { AuthContext } from "../context/Auth/AuthContext";
import { useActiveMenu } from "../context/ActiveMenuContext";
import useVersion from "../hooks/useVersion";
import useNavGroups from "../hooks/useNavGroups";

const BADGE = {
  blue:  { bg: "rgba(3,153,255,0.20)",  text: "#60A5FA" },
  red:   { bg: "rgba(239,68,68,0.20)",  text: "#F87171" },
  green: { bg: "rgba(34,197,94,0.20)",  text: "#4ADE80" },
  amber: { bg: "rgba(245,158,11,0.20)", text: "#FBBF24" },
};

// Exportado (ademas de usarse aca) para que layout/index.js (menu "Mas" de
// mobile) use el mismo mapa -- una sola fuente de verdad para el icono de
// cada categoria, en vez de que cada superficie invente el suyo.
export const CATEGORY_ICONS = {
  atencion:       <HeadsetMicIcon />,
  llamadas_ia:    <PhoneInTalkIcon />,
  analisis:       <BarChartIcon />,
  automatizacion: <BoltOutlinedIcon />,
  admin:          <ManageAccountsOutlinedIcon />,
  sistema:        <SettingsOutlinedIcon />,
};

// ── NavItem (expanded mode) ─────────────────────────────────────
function NavItem({ item, drawerOpen, location }) {
  const theme = useTheme();
  const isActive = location.pathname === item.to ||
    (item.to !== "/" && location.pathname.startsWith(item.to));

  const renderLink = useMemo(
    () => React.forwardRef((p, ref) => <RouterLink to={item.to} ref={ref} {...p} />),
    [item.to]
  );

  const inner = (
    <ListItemButton component={renderLink} sx={{
      py: "5px", px: "8px", borderRadius: "8px", minHeight: 33, gap: "9px",
      mx: "4px", width: "calc(100% - 8px)", position: "relative",
      transition: "background 0.12s ease",
      background: isActive ? "rgba(3,153,255,0.09)" : "transparent",
      "&::before": isActive ? {
        content: '""', position: "absolute", left: 0, top: "50%",
        transform: "translateY(-50%)", width: "2.5px", height: "18px",
        background: "#0399FF", borderRadius: "0 2px 2px 0",
      } : {},
      "&:hover": { background: isActive ? "rgba(3,153,255,0.11)" : (theme.palette.mode==="dark"?"rgba(255,255,255,0.05)":"rgba(0,0,0,0.05)") },
    }}>
      <Box sx={{
        color: isActive ? "#0399FF" : "rgba(255,255,255,0.40)",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 16, width: 17, flexShrink: 0, transition: "color 0.12s",
        "& svg": { fontSize: "1.05rem" },
      }}>
        {item.icon}
      </Box>
      {drawerOpen && (
        <>
          <Typography sx={{
            color: isActive ? "rgba(255,255,255,0.88)" : "rgba(255,255,255,0.55)",
            fontSize: 12.5, flex: 1, fontWeight: isActive ? 500 : 400,
            lineHeight: 1.2, transition: "color 0.12s",
          }}>
            {item.label}
          </Typography>
          {item.badge > 0 && item.badgeColor && (
            <Box sx={{
              background: BADGE[item.badgeColor].bg, color: BADGE[item.badgeColor].text,
              fontSize: 9, px: "5px", py: "1px", borderRadius: "9px",
              fontWeight: 700, lineHeight: 1.5, flexShrink: 0,
            }}>
              {item.badge}
            </Box>
          )}
        </>
      )}
    </ListItemButton>
  );

  if (!drawerOpen) {
    return <Tooltip title={item.label} placement="right" arrow disableInteractive>{inner}</Tooltip>;
  }
  return inner;
}

// ── NavGroup (expanded mode) ────────────────────────────────────
function NavGroup({ group, drawerOpen, isOpen, onToggle, location }) {
  const theme = useTheme();
  if (!drawerOpen) {
    return (
      <>
        {group.items.filter(i => !i.hidden).map(item => (
          <NavItem key={item.to} item={item} drawerOpen={false} location={location} />
        ))}
        <Divider sx={{ mx: "8px", my: 0.6, opacity: 0.10 }} />
      </>
    );
  }
  return (
    <>
      <Box onClick={onToggle} sx={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        px: "12px", py: "5px", mt: "6px", cursor: "pointer",
        "&:hover .grp-chevron": { opacity: 0.7 },
      }}>
        <Typography sx={{
          color: "rgba(255,255,255,0.55)", fontSize: 10, fontWeight: 600,
          letterSpacing: "0.08em", textTransform: "uppercase",
        }}>
          {group.label}
        </Typography>
        <Box className="grp-chevron" sx={{ color: (theme.palette.mode==="dark"?"rgba(255,255,255,0.55)":"rgba(0,0,0,0.55)"), opacity: 0.7, display: "flex", transition: "opacity 0.12s" }}>
          {isOpen ? <ExpandLessIcon sx={{ fontSize: 15 }} /> : <ExpandMoreIcon sx={{ fontSize: 15 }} />}
        </Box>
      </Box>
      <Collapse in={isOpen} timeout={150} unmountOnExit>
        {group.items.filter(i => !i.hidden).map(item => (
          <NavItem key={item.to} item={item} drawerOpen={true} location={location} />
        ))}
      </Collapse>
    </>
  );
}

// ── SubIconBtn (collapsed sub-column) ──────────────────────────
function SubIconBtn({ item, location }) {
  const theme = useTheme();
  const isActive = location.pathname === item.to ||
    (item.to !== "/" && location.pathname.startsWith(item.to));

  const renderLink = useMemo(
    () => React.forwardRef((p, ref) => <RouterLink to={item.to} ref={ref} {...p} />),
    [item.to]
  );

  return (
    <Tooltip title={item.label} placement="right" arrow disableInteractive>
      <ListItemButton component={renderLink} sx={{
        width: 56, height: 36, borderRadius: "8px", p: 0,
        minWidth: 0, mx: "auto", mb: "2px",
        display: "flex", alignItems: "center", justifyContent: "center",
        background: isActive ? "rgba(3,153,255,0.12)" : "transparent",
        color: isActive ? "#0399FF" : "rgba(255,255,255,0.40)",
        transition: "background 0.12s, color 0.12s",
        "& svg": { fontSize: "1.15rem" },
        "&:hover": { background: (theme.palette.mode==="dark"?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)"), color: (theme.palette.mode==="dark"?"rgba(255,255,255,0.70)":"rgba(0,0,0,0.70)") },
      }}>
        {item.icon}
      </ListItemButton>
    </Tooltip>
  );
}

// ── Avatar footer (shared) ──────────────────────────────────────
function AvatarFooter({ userName, userInitials, drawerOpen, onProfileClick, userProfile }) {
  const theme = useTheme();
  return (
    <Box sx={{ p: "6px 7px", borderTop: `0.5px solid ${theme.palette.mode==="dark"?"rgba(255,255,255,0.05)":"rgba(0,0,0,0.05)"}`, flexShrink: 0 }}>
      <Box onClick={onProfileClick} sx={{
        display: "flex", alignItems: "center", gap: "8px",
        p: drawerOpen ? "5px 6px" : "5px 3px",
        borderRadius: "8px", cursor: "pointer",
        justifyContent: drawerOpen ? "flex-start" : "center",
        "&:hover": { background: (theme.palette.mode==="dark"?"rgba(255,255,255,0.05)":"rgba(0,0,0,0.05)") },
        transition: "background 0.12s",
      }}>
        <Box sx={{ position: "relative", flexShrink: 0 }}>
          <Avatar sx={{
            width: 36, height: 36, fontSize: 12, fontWeight: 700,
            background: "linear-gradient(135deg,#1e3a5f,#0d2a4a)",
            border: "1.5px solid rgba(96,165,250,0.25)",
          }}>
            {userInitials}
          </Avatar>
          <Box sx={{
            width: 8, height: 8, borderRadius: "50%", background: "#22c55e",
            position: "absolute", bottom: 0, right: 0,
            border: "1.5px solid rgba(13,22,40,0.98)",
          }} />
        </Box>
        {drawerOpen && (
          <>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ color: "rgba(255,255,255,0.80)", fontSize: 11.5, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {userName}
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: 10 }}>
                {userProfile === "admin" ? "Admin" : "Agente"} · En línea
              </Typography>
            </Box>
            <MoreVertIcon sx={{ color: "rgba(255,255,255,0.40)", fontSize: 14, flexShrink: 0 }} />
          </>
        )}
      </Box>
    </Box>
  );
}

// ── MainListItems ────────────────────────────────────────────────
const MainListItems = ({ drawerOpen, searchQuery, setSearchQuery, onProfileClick, onCatChange }) => {
  const theme = useTheme();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const { setActiveMenu } = useActiveMenu();
  const searchRef = useRef(null);

  // RDS-NAVGROUPS: feature flags, chats/badge, isAdmin y navGroups ahora viven
  // en el hook compartido useNavGroups (unico origen de datos junto al menu
  // movil "Mas" de layout/index.js).
  const { navGroups } = useNavGroups();
  const [version, setVersion] = useState("");

  // Expanded groups state
  const [openGroups, setOpenGroups] = useState({
    atencion: true, analisis: true, automatizacion: true, admin: false, sistema: false,
  });

  // Collapsed rail: which category sub-column is open
  const [activeCat, setActiveCat] = useState(null);

  const { getVersion } = useVersion();

  const toggleGroup = (id) => setOpenGroups(prev => ({ ...prev, [id]: !prev[id] }));

  useEffect(() => {
    if (location.pathname.startsWith("/tickets")) setActiveMenu("/tickets");
    else setActiveMenu("");
  }, [location, setActiveMenu]);

  useEffect(() => { getVersion().then(v => setVersion(v.version)); }, []);

  // ⌘K
  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setTimeout(() => searchRef.current?.focus(), 50);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);


  const filteredItems = searchQuery
    ? navGroups.flatMap(g =>
        g.items.filter(i => i.label?.toLowerCase().includes(searchQuery.toLowerCase()))
          .map(i => ({ ...i, _groupLabel: g.label }))
      )
    : null;

  const userName = user?.name || user?.email || "Usuario";
  const userInitials = userName.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();

  // ── Which category contains the active route (for collapsed mode) ──
  const activeCatForRoute = useMemo(() => {
    for (const g of navGroups) {
      if (g.items.some(i => i.to === location.pathname || (i.to !== "/" && location.pathname.startsWith(i.to)))) {
        return g.id;
      }
    }
    return null;
  }, [navGroups, location.pathname]);

  // Close sub-column when drawer opens
  useEffect(() => {
    if (drawerOpen && activeCat) {
      // Sync: open the matching group in expanded mode
      setOpenGroups(prev => ({ ...prev, [activeCat]: true }));
      setActiveCat(null);
    }
  }, [drawerOpen]);
  useEffect(() => { onCatChange?.(activeCat); }, [activeCat]);

  // Close flyout when clicking outside sidebar in collapsed mode
  useEffect(() => {
    if (drawerOpen) return;
    const handler = (e) => {
      if (!e.target.closest || e.clientX > 104) setActiveCat(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [drawerOpen]);

  // ── COLLAPSED RENDER ─────────────────────────────────────────────
  if (!drawerOpen) {
    const activeGroup = navGroups.find(g => g.id === activeCat);
    const isCatActive = (catId) => catId === activeCatForRoute;

    return (
      <Box sx={{ display: "flex", height: "100%", position: "relative" }}>
        {/* Primary rail — 52px */}
        <Box sx={{ width: 52, display: "flex", flexDirection: "column", height: "100%", flexShrink: 0 }}>
          {/* Logo */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 42, flexShrink: 0, borderBottom: `0.5px solid ${theme.palette.mode==="dark"?"rgba(255,255,255,0.04)":"rgba(0,0,0,0.04)"}` }}>
            <Box component="img" src={isotipo} alt="Shessai" sx={{ height: 28, width: 28, objectFit: "contain" }} />
          </Box>

          {/* Category buttons */}
          <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", pt: "4px", pb: "4px", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
            {navGroups.filter(g => g.items.length > 0).map(g => {
              const isOpen = activeCat === g.id;
              const hasActive = isCatActive(g.id);

              // Grupos con un solo ítem navegan directo, sin flyout intermedio
              if (g.items.length === 1) {
                const item = g.items[0];
                const isActive = location.pathname === item.to ||
                  (item.to !== "/" && location.pathname.startsWith(item.to));
                return (
                  <Tooltip key={g.id} title={item.label} placement="right" arrow disableInteractive>
                    <Box component={RouterLink} to={item.to} sx={{
                      width: 38, height: 38, borderRadius: "10px",
                      mx: "auto", mb: "3px", textDecoration: "none",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      position: "relative",
                      background: isActive ? "rgba(3,153,255,0.15)" : "transparent",
                      color: isActive ? "#0399FF" : "rgba(255,255,255,0.40)",
                      "& svg": { fontSize: "1.35rem" },
                      transition: "background 0.12s, color 0.12s",
                      "&:hover": { background: (theme.palette.mode==="dark"?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)"), color: (theme.palette.mode==="dark"?"rgba(255,255,255,0.65)":"rgba(0,0,0,0.65)") },
                      ...(isActive && {
                        "&::before": {
                          content: '""', position: "absolute",
                          left: 0, top: "50%", transform: "translateY(-50%)",
                          width: "3px", height: "22px",
                          background: "#0399FF", borderRadius: "0 3px 3px 0",
                        },
                      }),
                    }}>
                      {CATEGORY_ICONS[g.id]}
                    </Box>
                  </Tooltip>
                );
              }

              return (
                <Tooltip key={g.id} title={g.label} placement="right" arrow disableInteractive>
                  <Box onClick={() => setActiveCat(isOpen ? null : g.id)} sx={{
                    width: 38, height: 38, borderRadius: "10px",
                    mx: "auto", mb: "3px", cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    position: "relative",
                    background: isOpen ? "rgba(3,153,255,0.15)" : "transparent",
                    color: isOpen || hasActive ? "#0399FF" : "rgba(255,255,255,0.40)",
                    "& svg": { fontSize: "1.35rem" },
                    transition: "background 0.12s, color 0.12s",
                    "&:hover": { background: (theme.palette.mode==="dark"?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)"), color: (theme.palette.mode==="dark"?"rgba(255,255,255,0.65)":"rgba(0,0,0,0.65)") },
                    ...(isOpen && {
                      "&::before": {
                        content: '""', position: "absolute",
                        left: 0, top: "50%", transform: "translateY(-50%)",
                        width: "3px", height: "22px",
                        background: "#0399FF", borderRadius: "0 3px 3px 0",
                      },
                    }),
                  }}>
                    {CATEGORY_ICONS[g.id]}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>

          {/* Avatar always at bottom */}
          <AvatarFooter
            userName={userName}
            userInitials={userInitials}
            drawerOpen={false}
            onProfileClick={onProfileClick}
            userProfile={user.profile}
          />
        </Box>

        {/* Secondary flyout — fixed at left:52, escapes overflow:hidden parent */}
        <Box sx={{
          position: "fixed",
          left: 52, top: 0,
          width: activeCat ? 80 : 0,
          height: "100vh",
          overflow: "hidden",
          transition: "width 0.2s cubic-bezier(0.4,0,0.2,1)",
          background: "rgba(17,24,39,0.98)",
          
          borderRight: `0.5px solid ${theme.palette.mode==="dark"?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.08)"}`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          zIndex: 1201,
        }}>
          {activeCat && activeGroup && (
            <Box sx={{ width: 80, pt: "10px", pb: "4px", display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Typography sx={{
                color: "rgba(255,255,255,0.55)", fontSize: 9, fontWeight: 700,
                letterSpacing: "0.08em", textTransform: "uppercase",
                textAlign: "center", px: "4px", mb: "8px",
                whiteSpace: "nowrap",
              }}>
                {activeGroup.label}
              </Typography>
              {activeGroup.items.filter(i => !i.hidden).map(item => (
                <SubIconBtn key={item.to} item={item} location={location} />
              ))}
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  // ── EXPANDED RENDER ──────────────────────────────────────────────
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Search bar */}
      <Box sx={{ px: "8px", pt: "6px", pb: "4px", flexShrink: 0 }}>
        <Box sx={{
          display: "flex", alignItems: "center", gap: "6px",
          background: (theme.palette.mode==="dark"?"rgba(255,255,255,0.04)":"rgba(0,0,0,0.04)"),
          border: `0.5px solid ${searchQuery ? "rgba(3,153,255,0.40)" : "rgba(255,255,255,0.40)"}`,
          borderRadius: "8px", px: "9px", py: "5px",
          transition: "border-color 0.15s",
        }}>
          <SearchIcon sx={{ color: searchQuery ? "#0399FF" : "rgba(255,255,255,0.40)", fontSize: 13, flexShrink: 0 }} />
          <InputBase
            inputRef={searchRef}
            placeholder="Buscar sección..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            sx={{ flex: 1, fontSize: 12, color: "rgba(255,255,255,0.80)", "& input::placeholder": { color: "rgba(255,255,255,0.55)" } }}
          />
          {searchQuery ? (
            <IconButton size="small" onClick={() => setSearchQuery("")} sx={{ p: 0 }}>
              <CloseIcon sx={{ fontSize: 11, color: "rgba(255,255,255,0.40)" }} />
            </IconButton>
          ) : (
            <Box sx={{ display: "flex", gap: "2px", flexShrink: 0 }}>
              {["⌘","K"].map(k => (
                <Box key={k} sx={{ background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.55)", fontSize: 9, px: "4px", py: "1px", borderRadius: "3px", fontFamily: "monospace" }}>
                  {k}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* Nav list */}
      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", py: "2px", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
        {filteredItems ? (
          <>
            {filteredItems.length === 0 ? (
              <Typography sx={{ color: (theme.palette.mode==="dark"?"rgba(255,255,255,0.25)":"rgba(0,0,0,0.25)"), fontSize: 11, textAlign: "center", py: 3 }}>Sin resultados</Typography>
            ) : filteredItems.map(item => (
              <NavItem key={item.to} item={item} drawerOpen={true} location={location} />
            ))}
          </>
        ) : (
          navGroups.filter(g => g.items.length > 0).map(group =>
            group.items.length === 1 ? (
              <NavItem key={group.items[0].to} item={group.items[0]} drawerOpen={true} location={location} />
            ) : (
              <NavGroup
                key={group.id}
                group={group}
                drawerOpen={true}
                isOpen={openGroups[group.id]}
                onToggle={() => toggleGroup(group.id)}
                location={location}
              />
            )
          )
        )}
      </Box>

      {/* Version */}
      {version && (
        <Typography sx={{ color: "rgba(255,255,255,0.40)", fontSize: 9, textAlign: "center", py: "4px", flexShrink: 0 }}>
          v{version}
        </Typography>
      )}

      {/* Avatar */}
      <AvatarFooter
        userName={userName}
        userInitials={userInitials}
        drawerOpen={true}
        onProfileClick={onProfileClick}
        userProfile={user.profile}
      />
    </Box>
  );
};

export default MainListItems;
